import { addDays, compareAsc, isBefore, isSameDay, parseISO } from "date-fns";
import { useParams } from "react-router-dom";
import {
  useBooths,
  useSession,
  useSessions,
  useUser,
  useVenue,
} from "./helpers";
import { LoadingParams } from "../../app/types";
import now from "../../util/now";
import useSessionId from "../sessions/useSessionId";

/* Sessions */
export const usePresentingSessionsTodayOrTomorrow = () => {
  const sessions = useSessions();
  const sessionId = useSessionId();
  const user = useUser();
  if (!user || !sessions) {
    return [];
  }
  return sessions
    .filter((session) =>
      session.presenters
        .map((presenter) => presenter.profileId)
        .includes(user.profileId),
    )
    .filter((session) => {
      const start = parseISO(session.startTime);
      return isSameDay(start, now) || isSameDay(start, addDays(now, 1));
    })
    .filter((session) => session.sessionId !== sessionId)
    .sort((a, b) =>
      isBefore(parseISO(a.startTime), parseISO(b.startTime)) ? -1 : 1,
    );
};

export const useSessionsByDay = (day: Date | null) => {
  const sessions = useSessions();
  if (day === null) {
    return [];
  }
  return sessions
    .filter((session) => isSameDay(parseISO(session.startTime), day))
    .sort((a, b) => compareAsc(parseISO(a.startTime), parseISO(b.startTime)));
};

export const useAllTags = () =>
  useSessions()
    .map((session) => session.tags)
    .reduce(
      (previous, current) => Array.from(new Set([...previous, ...current])),
      [],
    );

export const useKeynotes = () =>
  useSessions().filter((session) => session.isKeynote);

export const useIsPresenting = () => {
  const session = useSession();
  const booth = useBooth();
  const user = useUser();
  if (!user) {
    return false;
  }

  const isPresentingBooth =
    booth &&
    booth.presenters
      .map((presenter) => presenter.profileId)
      .includes(user.profileId);
  const isPresentingSession =
    session &&
    session.presenters
      .map((presenter) => presenter.profileId)
      .includes(user.profileId);
  return isPresentingBooth || isPresentingSession;
};

/* Booths */
export const useBooth = () => {
  const params = useParams<LoadingParams>();
  const boothId = params.boothId ? parseInt(params.boothId, 10) : -1;
  return (useBooths() ?? []).find((booth) => booth.boothId === boothId);
};

/* Sponsors */
export const useSponsors = (level?: number) => {
  const venue = useVenue();
  const sponsors = venue?.sponsors ?? [];
  if (level === undefined) {
    return sponsors;
  }

  if (level === 2) {
    return sponsors
      .filter((sponsor) => +sponsor.level > 1)
      .sort((a, b) => b.level - a.level);
  }
  return sponsors.filter((sponsor) => +sponsor.level === level);
};
